import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { HttpStatusCode } from "axios";
import clsx from "clsx";

import {
  EvacuationStatusType,
  EvacueeItems,
  EvacueeListRequest,
  EvacueeListType,
} from "../../../../../types/emergency";

import {
  EvacuationGroup,
  EvacueeListCount,
  getEvacuationGroupBuildingList,
  getEvacueeList,
  getEvacueeListCount,
  getSituationDetail,
  ShelterItem,
  SituationDetailItem,
} from "../../../../../services/managerEmergencyServices";
import { Pageable } from "../../../../../services/talkServices";

import { RefreshIcon } from "../../../../../hooks/useStoreHeader";
import useEvacuationManger from "../../../../../hooks/useEvacuationManager";
import useFullScreenLoading from "../../../../../hooks/useFullScreenLoading";
import useSimplePopup from "../../../../../hooks/useSimplePopup";

import StickyHeader from "../../../../../components/Layout/StickyHeader";
import ImagePreview from "../../../../../components/Input/ImagePreview";
import EmergencyCheckPopup from "../../../../../components/Popup/EmergencyCheck";
import RegistrationForm from "../../../../../components/Popup/CreateEvacuee";
import HorizontalRule from "../../../../../components/Rule/Horizontal";
import StackedBar from "./StackedBar";
import CommonButton from "../../../../../components/Button";
import CommonInput from "../../../../../components/Input/CommonInput";
import { Skeleton } from "../../../../Talk";
import CommonPopup from "../../../../../components/Popup";

import detailCallIcon from "../../../../../assets/img/emergency/detail-call.png";
import BluetoothIcon from "../../../../../assets/img/emergency/emergency-three-bluetooth-icon.png";
import QrIcon from "../../../../../assets/img/emergency/emergency-three-qr-icon.png";
import { FaPen } from "react-icons/fa";
import ZoomSearchIcon from "../../../../../svgs/icons/ZoomSearchIcon";

type Props = {};

const initialData: { [key: string]: string | number } = {
  name: "",
  대피완료: 0,
  구조요청: 0,
  휴가외근: 0,
  무응답: 0,
};

const initPagination = {
  total: 0,
  pageSize: 10,
  page: 1,
  sort: null,
  offset: 0,
  totalPages: 0,
};

export default function EmergencyManagerSituationDetail(props: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [simplePopupComponent, { open, openError }] = useSimplePopup({});
  const [isManager, isUser, { user }] = useEvacuationManger();

  const [evacueeDetailLoading, setEvacueeDetailLoading] =
    useState<boolean>(false);
  const [evacueeCountLoading, setEvacueeCountLoading] =
    useState<boolean>(false);
  const [situationLoading, setSituationLoading] = useState<boolean>(false);
  const [floorLoading, setFloorLoading] = useState<boolean>(false);
  const loadingComponent = useFullScreenLoading({
    isLoading:
      evacueeDetailLoading ||
      evacueeCountLoading ||
      situationLoading ||
      floorLoading ||
      (user?.isLoading ?? false),
  });

  const [detail, setDetail] = useState<SituationDetailItem | undefined>(
    undefined
  );
  const [groupBuilding, setGroupBuilding] = useState<
    EvacuationGroup | undefined
  >(undefined);
  const [selectedShelter, setSelectedShelter] = useState<
    ShelterItem | undefined
  >(undefined);
  const [tempSelectedShelterId, setTempSelectedShelterId] = useState<
    string | undefined
  >(undefined);
  const [evacuationCount, setEvacuationCount] =
    useState<EvacueeListCount | null>(null); // null로 초기화
  const [data, setData] = useState<{ [key: string]: string | number }>(
    initialData
  );
  const [ignoreManager, setIgnoreManager] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>("전체");
  const [searchTable, setSearchTable] = useState<Partial<EvacueeListRequest>>({
    status: undefined,
  });
  const [filteredEvacuationDetails, setFilteredEvacuationDetails] = useState<
    EvacueeListType["items"]
  >([]);
  const [isRegistrationFormOpen, setIsRegistrationFormOpen] = useState(false);
  const [searchTableName, setSearchTableName] = useState<string>("");
  const [initialTotalCount, setInitialTotalCount] = useState<number | null>(
    null
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [popup, setPopup] = useState<string>("");
  const [selectedEvacuee, setSelectedEvacuee] = useState<
    EvacueeItems | undefined
  >(undefined);

  const [pagination, setPagination] = useState<Pageable>(initPagination);

  const pageNumbers = [];
  const startPage = Math.max(1, currentPage - 1);
  const endPage = Math.min(pagination.totalPages, currentPage + 1);

  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  const handleRefresh = () => {
    loadSituationDetail();
    refreshData();
  };

  // 부모 컴포넌트에서 새로고침 함수 정의
  const refreshData = async () => {
    await loadEvacueeDetail(currentPage); // 대피자 세부 정보를 다시 로드
    await loadEvacueeCountDetail(); // 대피자 수 세부 정보를 다시 로드
  };

  const handleSearch = () => {
    setSearchTable((prev) => ({ ...prev, name: searchTableName }));
    setCurrentPage(1); // 검색어가 변경되면 페이지를 1로 설정
  };

  // RegistrationForm 팝업을 열기 위한 함수
  const openRegistrationForm = () => {
    setIsRegistrationFormOpen(true);
  };

  // RegistrationForm 팝업을 닫기 위한 함수
  const closeRegistrationForm = () => {
    setIsRegistrationFormOpen(false);
  };

  const loadSituationDetail = async () => {
    if (!params.id) return;
    try {
      setSituationLoading(true);
      const response = await getSituationDetail(params.id);
      if (response.status === HttpStatusCode.Ok) {
        setDetail(response.data);
        setSearchTable((prev) => ({
          ...prev,
          evacuationGroupId: response.data.evacuationGroupId,
          evacuationId: response.data.evacuationId,
        }));
        setSelectedShelter(
          response.data.shelters.find((shelter) => shelter.isDefaultShelter)
        );
      } else {
        throw new Error("상황 정보를 불러오지 못했습니다.");
      }
    } catch (e: any) {
      openError(e, "상황 정보를 불러오지 못했습니다.");
    } finally {
      setSituationLoading(false);
    }
  };

  const loadEvacueeDetail = async (page: number = 1) => {
    if (!detail) return;
    if (!searchTable.evacuationGroupId || !searchTable.evacuationId) return;
    try {
      setEvacueeDetailLoading(true);
      const response = await getEvacueeList({
        evacuationGroupId: searchTable.evacuationGroupId,
        evacuationId: searchTable.evacuationId,
        status: searchTable.status,
        name: searchTable.name,
        page: page,
        pageSize: pagination.pageSize,
        evacuationCompanyId: searchTable.evacuationCompanyId,
        evacuationDepartmentName: searchTable.evacuationDepartmentName,
        ignoreManager,
      });
      if (response.status === HttpStatusCode.Ok) {
        setFilteredEvacuationDetails(response.data.items);
        setPagination(response.data.pageable);
      } else {
        throw new Error("상황 참여자 정보를 불러오지 못했습니다.");
      }
    } catch (e: any) {
      openError(e, "상황 참여자 정보를 불러오지 못했습니다.");
    } finally {
      setEvacueeDetailLoading(false);
    }
  };

  const loadGroupBuilding = async (evacuationGroupId: string) => {
    try {
      const response = await getEvacuationGroupBuildingList(evacuationGroupId);
      if (response.status === HttpStatusCode.Ok) {
        setGroupBuilding(response.data);
      } else {
        throw new Error("그룹 빌딩 정보를 불러오지 못했습니다.");
      }
    } catch (e: any) {
      openError(e, "그룹 빌딩 정보를 불러오지 못했습니다.");
    }
  };

  const loadEvacueeCountDetail = async () => {
    if (!detail) return;
    try {
      setEvacueeCountLoading(true);
      const response = await getEvacueeListCount({
        evacuationGroupId: detail.evacuationGroupId,
        evacuationId: detail.evacuationId,
        ignoreManager: ignoreManager,
      });
      if (response.status === HttpStatusCode.Ok) {
        const countData = response.data;

        // 처음 로드될 때만 실행되도록 수정
        if (!ignoreManager) {
          const newData = {
            name: "비상대피 현황",
            대피완료: countData.evacuationCompleteCount ?? 0,
            구조요청: countData.helpMeCount ?? 0,
            휴가외근: countData.onLeaveOrOUtCount ?? 0,
            무응답: countData.createNoResponseCount ?? 0,
          };

          setData(newData);
          setInitialTotalCount(countData.totalCount ?? 0);
          setEvacuationCount(countData);
        }

        setEvacuationCount(countData); // 기존 상태 업데이트
      } else {
        throw new Error("상황 참여자 현황을 불러오지 못했습니다.");
      }
    } catch (e: any) {
      openError(e, "상황 참여자 현황을 불러오지 못했습니다.");
    } finally {
      setEvacueeCountLoading(false);
    }
  };

  const handleClickUserStatus = (item: EvacueeItems) => {
    setPopup("statusChange");
    setSelectedEvacuee(item);
  };

  const handleCloseStatusChangePopup = () => {
    setPopup("");
    setSelectedEvacuee(undefined);
  };

  const handleBLEClick = () => {
    const { shelterId } = detail?.shelters?.find(
      (shelter) => shelter.isDefaultShelter
    ) ?? { shelterId: undefined };
    console.log("Shelter ID:", shelterId);

    if (shelterId) {
      navigate("/manager/evacuation-manager/confirm-ble", {
        state: {
          shelterId,
          createdAdminId: user.memberId,
        },
      });
    } else {
      open("기본 대피소 정보가 없습니다.");
    }
  };

  const handleQrClick = () => {
    const { shelterId } = detail?.shelters?.find(
      (shelter) => shelter.isDefaultShelter
    ) ?? { shelterId: undefined };
    const createdAdminId = user?.memberId ?? null;

    if (shelterId && createdAdminId !== undefined) {
      navigate("/manager/evacuation-manager/confirm-qr", {
        state: { shelterId, createdAdminId: user.memberId },
      });
    } else {
      console.error("shelterId 또는 createdAdminId가 유효하지 않습니다.");
    }
  };

  useEffect(() => {
    if (detail?.evacuationGroupId) {
      loadGroupBuilding(detail?.evacuationGroupId);
    }
  }, [detail?.evacuationGroupId]);

  useEffect(() => {
    loadSituationDetail();
  }, []);

  useEffect(() => {
    if (detail) {
      loadEvacueeCountDetail();
    }
  }, [detail, ignoreManager]);

  useEffect(() => {
    if (searchTable.evacuationGroupId && searchTable.evacuationId) {
      loadEvacueeDetail(currentPage);
      loadEvacueeCountDetail();
    }
  }, [currentPage, searchTable, ignoreManager]);

  const refreshEvacuationDetails = async () => {
    await loadEvacueeDetail(currentPage);
    await loadEvacueeCountDetail();
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIgnoreManager(event.target.checked);
  };

  const handleConfirm = async (uuid: string) => {
    // 팝업에서 확인 버튼을 눌렀을 때 호출됨
    await refreshEvacuationDetails(); // 데이터 새로 로드
    setPopup("");
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const convertEmergencyStatusToText = (status: string) => {
    switch (status) {
      case "CREATED_NO_RESPONSE":
        return "무응답";
      case "EVACUATION_COMPLETE":
        return "대피완료";
      case "ON_LEAVE_OR_OUT":
        return "휴가/외근";
      case "HELP_ME":
        return "구조요청";
      default:
        return "";
    }
  };

  const convertTextToEmergencyStatus = (statusText: string) => {
    switch (statusText) {
      case "무응답":
        return "CREATED_NO_RESPONSE";
      case "대피완료":
        return "EVACUATION_COMPLETE";
      case "휴가/외근":
        return "ON_LEAVE_OR_OUT";
      case "구조요청":
        return "HELP_ME";
      default:
        return undefined;
    }
  };

  const convertEmergencyStatusToColor = (status: string) => {
    switch (status) {
      case "CREATED_NO_RESPONSE":
        return "text-brand-neutral-50";
      case "EVACUATION_COMPLETE":
        return "text-brand-cleaning-badge";
      case "ON_LEAVE_OR_OUT":
        return "text-[#0496FF]";
      case "HELP_ME":
        return "text-[#FF4343]";
      default:
        return "";
    }
  };

  const renderStatusButton = (item: EvacueeItems) => {
    const text = convertEmergencyStatusToText(item.status);
    const color = convertEmergencyStatusToColor(item.status);
    const isButtonViewing = detail?.status !== "CREATED"; // 생성됨 이 아니면 버튼을 볼 수 있다.
    return (
      <>
        {isButtonViewing ? (
          <div
            className="flex flex-row text-xs border rounded-[3px] px-2 py-1 items-center"
            onClick={() => handleClickUserStatus(item)}
          >
            <FaPen className="w-[9px] text-gray-400 mr-1 flex flex-shrink-0" />
            <div className={clsx("font-semibold", color)}>{text}</div>
          </div>
        ) : (
          <div className="flex flex-row text-xs px-2 py-1 items-center">
            <div className={clsx("font-semibold", color)}>{text}</div>
          </div>
        )}
      </>
    );
  };

  return (
    <>
      {loadingComponent}
      <div className="px-5">
        <StickyHeader
          title="비상대피 관리"
          rightContent={
            <div onClick={handleRefresh}>
              <RefreshIcon />
            </div>
          }
          onBackClick={() => {
            navigate("/manager/evacuation-manager", { replace: true });
          }}
        />
      </div>
      <div className="min-h-screen w-screen flex flex-col items-center bg-white">
        {detail ? (
          <div className="p-5 flex flex-col flex-gap gap-1 w-full">
            <h2 className="text-lg leading-[21px] font-medium">
              {detail?.evacuationCategoryName}
            </h2>
            <h3 className=" text-3xl leading-[38px] font-light tracking-[-4%]">
              {detail?.evacuationName}
            </h3>
            <span className="font-normal text-xs leading-4 text-brand-neutral-50">{`${detail?.incidentAddress} ${detail?.incidentFloorName}층`}</span>
            {detail?.status !== "CREATED" && (
              <span className="font-normal text-xs leading-4 text-brand-neutral-50">
                {formatDateTime(detail?.startedAt ?? "")}
              </span>
            )}
          </div>
        ) : (
          <div className="p-5 flex flex-col flex-gap gap-1 w-full">
            <Skeleton className="w-[50px] h-[21px] rounded-3xl" />
            <Skeleton className="w-[200px] h-[38px] rounded-3xl" />
            <Skeleton className="w-[120px] h-[16px] rounded-3xl" />
            <Skeleton className="w-[150px] h-[16px] rounded-3xl" />
          </div>
        )}

        <div className="px-5 pb-6 w-full">
          <div className="p-5 rounded-[10px] shadow">
            <div className="flex justify-between items-center">
              <span
                className={clsx([
                  "text-brand-neutral-90 text-sm leading-[19px]",
                  {
                    "text-brand-primary-magenta": detail?.status === "CREATED",
                  },
                ])}
              >
                준비
              </span>
              <span
                className={clsx([
                  "text-brand-neutral-90 text-sm leading-[19px]",
                  {
                    "text-brand-primary-magenta":
                      detail?.status === "IN_PROGRESS",
                  },
                ])}
              >
                진행중
              </span>
              <span
                className={clsx([
                  "text-brand-neutral-90 text-sm leading-[19px]",
                  {
                    "text-brand-primary-magenta": detail?.status === "ENDED",
                  },
                ])}
              >
                종료
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
              <div
                className={`bg-pink-500 h-2.5 rounded-full ${
                  detail?.status === "CREATED"
                    ? "w-1/3"
                    : detail?.status === "IN_PROGRESS"
                    ? "w-2/3"
                    : "w-full"
                }`}
              ></div>
            </div>
          </div>
        </div>

        <div className="flex flex-col px-5 pb-5 items-start w-full">
          <h4 className="pb-2.5 font-medium text-lg leading-[21px] text-black">
            진행 내역
          </h4>
          <HorizontalRule className="bg-black" margin="0" />
          <div className="flex gap-2.5 min-h-[58px] items-center">
            <div className="w-[90px] text-[#6E6E70] text-sm leading-[18px]">
              상황 유형
            </div>
            <div className=" font-bold text-base leading-[26px] text-brand-neutral-100">
              {detail?.evacuationCategoryName ? (
                detail?.evacuationCategoryName
              ) : (
                <Skeleton className="w-[50px] h-[21px] rounded-3xl" />
              )}
            </div>
          </div>
          <HorizontalRule className="bg-brand-neutral-10" margin="0" />
          <div className="flex gap-2.5 min-h-[58px] items-center">
            <div className="min-w-[90px] text-[#6E6E70] text-sm leading-[18px]">
              상황명
            </div>
            <div className=" text-base leading-[26px] text-brand-neutral-100 py-[18px] break-all">
              {detail?.evacuationName ? (
                detail?.evacuationName
              ) : (
                <Skeleton className="w-[100px] h-[21px] rounded-3xl" />
              )}
            </div>
          </div>
          <HorizontalRule className="bg-brand-neutral-10" margin="0" />
          <div className="flex gap-2.5 min-h-[58px] items-center">
            <div className="min-w-[90px] text-[#6E6E70] text-sm leading-[18px]">
              발생 주소
            </div>
            <div className=" text-base leading-[26px] text-brand-neutral-100 py-[18px] break-all">
              {detail?.incidentAddress ? (
                detail?.incidentAddress
              ) : (
                <Skeleton className="w-[130px] h-[21px] rounded-3xl" />
              )}
            </div>
          </div>
          <HorizontalRule className="bg-brand-neutral-10" margin="0" />
          <div className="flex gap-2.5 min-h-[58px] items-center">
            <div className="min-w-[90px] text-[#6E6E70] text-sm leading-[18px]">
              발생 층
            </div>
            <div className=" text-base leading-[26px] text-brand-neutral-100 py-[18px] break-all">
              {detail?.incidentFloorName ? (
                detail?.incidentFloorName
              ) : (
                <Skeleton className="w-[30px] h-[21px] rounded-3xl" />
              )}
            </div>
          </div>
          <HorizontalRule className="bg-brand-neutral-10" margin="0" />
          {detail?.status !== "CREATED" && (
            <>
              <div className="flex gap-2.5 min-h-[58px] items-center">
                <div className="min-w-[90px] text-[#6E6E70] text-sm leading-[18px]">
                  발생 시각
                </div>
                <div className=" text-base leading-[26px] text-brand-neutral-100 py-[18px] break-all">
                  {detail?.startedAt ? (
                    formatDateTime(detail?.startedAt ?? "")
                  ) : (
                    <Skeleton className="w-[140px] h-[21px] rounded-3xl" />
                  )}
                </div>
              </div>
              <HorizontalRule className="bg-brand-neutral-10" margin="0" />
            </>
          )}
          <div className="flex flex-col gap-2.5 w-full items-start">
            <div className="flex gap-3 items-center">
              <div className="py-[18px] text-[#6E6E70] text-sm leading-[18px]">
                대피 장소
              </div>
              {(detail?.shelters?.length ?? 0) > 1 && (
                <CommonButton
                  variant="tertiary-s"
                  onClick={() => {
                    setPopup("shelterChange");
                  }}
                >
                  대피소 변경
                </CommonButton>
              )}
            </div>
            <div className=" text-base leading-[26px] text-brand-neutral-100 break-all">
              {selectedShelter?.shelterName ? (
                selectedShelter?.shelterName
              ) : (
                <Skeleton className="w-[100px] h-[26px] rounded-3xl" />
              )}
            </div>
            {selectedShelter?.shelterImg &&
              selectedShelter.shelterImg.items.length > 0 && (
                <div className="flex justify-center w-full">
                  <ImagePreview
                    key={0}
                    image={selectedShelter.shelterImg.items[1].url}
                    imageList={[selectedShelter.shelterImg.items[1].url]}
                    index={0}
                    wrapperClassName="w-full"
                    className="!w-full h-[56vw]"
                  />
                </div>
              )}
          </div>
          <HorizontalRule className="bg-brand-neutral-10" margin="0" />
        </div>

        <div className="flex flex-col w-full px-5">
          <div className="flex justify-between">
            <h4 className="pb-2.5 font-medium text-lg leading-[21px] text-black">
              비상대피 현황
            </h4>
            <span className="text-sm leading-[18px]">
              총 인원{" "}
              <strong className="font-semibold">{initialTotalCount}명</strong>
            </span>
          </div>
          <HorizontalRule className="bg-black" margin="0" />

          <div className="py-5 flex flex-col gap-2.5">
            <StackedBar
              data={[
                Number(data["대피완료"]),
                Number(data["구조요청"]),
                Number(data["휴가외근"]),
                Number(data["무응답"]),
              ]}
            />

            <div className="grid grid-cols-2 gap-4 text-sm leading-[18px]">
              <div className="flex flex-col">
                <div className="flex items-center py-[5px] gap-1 min-h-[44px]">
                  <span className="inline-block w-[14px] h-[14px] rounded bg-[#00CDC1]"></span>
                  <span className="text-brand-neutral-60">
                    대피완료 :{" "}
                    <span className="text-black">{data.대피완료}명</span>
                  </span>
                </div>
                <HorizontalRule margin="0" />
              </div>
              <div className="flex flex-col">
                <div className="flex items-center py-[5px] gap-1 min-h-[44px]">
                  <span className="inline-block w-[14px] h-[14px] rounded  bg-[#F87171]"></span>
                  <span className="text-brand-neutral-60">
                    구조요청 :{" "}
                    <span className="text-black">{data.구조요청}명</span>
                  </span>
                </div>
                <HorizontalRule margin="0" />
              </div>
              <div className="flex flex-col">
                <div className="flex items-center py-[5px] gap-1 min-h-[44px]">
                  <span className="inline-block w-[14px] h-[14px] rounded  bg-[#60A5FA]"></span>
                  <span className="text-brand-neutral-60">
                    휴가외근 :{" "}
                    <span className="text-black">{data.휴가외근}명</span>
                  </span>
                </div>
                <HorizontalRule margin="0" />
              </div>
              <div className="flex flex-col">
                <div className="flex items-center py-[5px] gap-1 min-h-[44px]">
                  <span className="inline-block w-[14px] h-[14px] rounded  bg-[#9CA3AF]"></span>
                  <span className="text-brand-neutral-60">
                    무응답 : <span className="text-black">{data.무응답}명</span>
                  </span>
                </div>
                <HorizontalRule margin="0" />
              </div>
            </div>
          </div>
        </div>

        <div className="px-5 pb-5 w-full">
          <div className="bg-[#f7f7f7] rounded-[3px] w-full p-5">
            <div
              className="flex items-center justify-between gap-2.5"
              onClick={handleBLEClick}
            >
              <img
                src={BluetoothIcon}
                alt="bluetooth-icon"
                className="h-[24px]"
              />
              <span className="flex-1">블루투스 인증</span>
            </div>
            <HorizontalRule />
            <div className="flex items-center gap-2.5" onClick={handleQrClick}>
              <img src={QrIcon} alt="qr-icon" className="h-[24px]" />
              QR 코드 인증
            </div>
          </div>
        </div>

        <div className="flex flex-col px-5 pb-5 gap-2.5">
          {/* 인원 등록 버튼 */}
          <div className="flex flex-col gap-2.5">
            <CommonButton
              variant="tertiary-s"
              className="self-end"
              onClick={openRegistrationForm}
              disabled={detail?.status === "ENDED"}
            >
              인원 등록
            </CommonButton>
            {/* RegistrationForm 호출 */}
            {detail && (
              <RegistrationForm
                isOpen={isRegistrationFormOpen}
                onClose={closeRegistrationForm}
                evacuationGroupId={detail.evacuationGroupId}
                evacuationId={detail.evacuationId}
                onRegistrationSuccess={refreshData} // 새로고침 함수 전달
                groupBuilding={groupBuilding}
                setFloorLoading={setFloorLoading}
              />
            )}
          </div>

          {/* 전체 건물/회사 선택 및 필터링 */}
          <div className="bg-white p-5 rounded-lg border flex flex-col gap-2.5">
            <div className="flex flex-row justify-end flex-wrap">
              <div className="flex items-center space-x-1.5">
                <input
                  type="checkbox"
                  className="form-checkbox h-4 w-4 text-gray-600"
                  checked={ignoreManager}
                  onChange={handleCheckboxChange} // 체크박스 상태 변경 이벤트 처리
                />
                <div className="text-gray-700 text-sm font-semibold">
                  관리자 제외
                </div>
              </div>
            </div>

            {/* 인원 현황 */}
            <div className="bg-[#F7F7F7] grid grid-cols-5 gap-x-0 rounded-[5px]">
              {["전체", "구조요청", "대피완료", "휴가/외근", "무응답"].map(
                (status, index) => {
                  let count = 0;
                  if (status === "전체") {
                    count = evacuationCount?.totalCount || 0;
                  } else if (status === "무응답") {
                    count = evacuationCount?.createNoResponseCount || 0;
                  } else if (status === "대피완료") {
                    count = evacuationCount?.evacuationCompleteCount || 0;
                  } else if (status === "휴가/외근") {
                    count = evacuationCount?.onLeaveOrOUtCount || 0;
                  } else if (status === "구조요청") {
                    count = evacuationCount?.helpMeCount || 0;
                  }

                  return (
                    <div
                      key={index}
                      className={clsx([
                        "flex flex-col items-center py-2 px-4 w-full border-gray-300 first:rounded-l-[5px] last:rounded-r-[5px]",
                        {
                          "bg-gray-300": selectedStatus === status,
                          "border-r": index < 4,
                        },
                      ])}
                      onClick={() => {
                        setSelectedStatus(status);
                        setCurrentPage(1);
                        setSearchTable((prev) => ({
                          ...prev,
                          status: convertTextToEmergencyStatus(status),
                        }));
                      }}
                      style={{ whiteSpace: "nowrap", textAlign: "center" }} // nowrap 추가
                    >
                      <span
                        className={`text-base font-bold ${
                          selectedStatus === status
                            ? "text-black"
                            : "text-gray-700"
                        }`}
                      >
                        {count}
                      </span>
                      <span className="text-xs truncate w-[calc(((100vw-80px)/5)-10px)]">
                        {status}
                      </span>
                    </div>
                  );
                }
              )}
            </div>
          </div>

          {/* 검색창 */}
          <div className="pb-1">
            <CommonInput
              placeholder="이름을 입력해주세요."
              className="p-4 text-base w-full bg-brand-neutral-05"
              postfix={{
                content: (
                  <div
                    onClick={() => {
                      handleSearch();
                    }}
                    className="opacity-60"
                  >
                    <ZoomSearchIcon />
                  </div>
                ),
                className: "px-3",
              }}
              value={searchTableName}
              onChange={(value: string) => setSearchTableName(value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
              variant="building"
            />
          </div>

          {/* 테이블 */}
          <HorizontalRule className="bg-black" margin="0" thickness={2} />
          <div className="bg-white">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="w-full border-b border-black">
                  <th className="text-xs font-normal text-gray-500 pb-2 text-start pl-1 w-[calc((100vw)/4)]">
                    건물/회사
                  </th>
                  <th className="text-center text-xs font-normal text-gray-500 pb-2 w-[calc((100vw)/6)]">
                    부서
                  </th>
                  <th className="text-center text-xs font-normal text-gray-500 pb-2 w-[calc((100vw)/5)]">
                    이름
                  </th>
                  <th className="text-center text-xs font-normal text-gray-500 pb-2 w-[calc((100vw)/5)]">
                    상태
                  </th>
                  <th className="text-center text-xs font-normal text-gray-500 pb-2 min-w-[25px]">
                    관리
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredEvacuationDetails?.map((item) => (
                  <tr key={item.evacuationEvacueeMapUuid} className="border-t">
                    <td>
                      <div className="flex flex-col justify-center pl-1">
                        <div className="truncate w-[calc((100vw)/4)] text-sm">
                          {item.evacuationBuildingName}
                        </div>
                        <span className="text-xs text-gray-600">
                          {item.evacuationCompanyName}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="flex flex-col justify-center items-center">
                        <span className="truncate w-[calc((100vw)/6)] text-center text-sm">
                          {item.evacuationDepartmentName}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="flex flex-col justify-center items-center">
                        <span className="truncate w-[calc((100vw)/5)] text-center text-sm">
                          {item.name}
                        </span>
                        {item.isManager && (
                          <div className="text-xs text-gray-600">(관리자)</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="flex justify-center items-center w-[calc((100vw)/5)]">
                        {renderStatusButton(item)}
                      </div>
                    </td>
                    <td>
                      <div className="flex justify-center items-center">
                        <a href={`tel:${item.mobileNumber}`}>
                          <button className="w-[20px]">
                            <img src={detailCallIcon} alt="detail-call-icon" />
                          </button>
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-center pt-1 w-full">
            <div
              id="pagination"
              className="flex flex-row w-full justify-between max-w-[90vw] flex-wrap"
            >
              <CommonButton
                className="text-sm flex-shrink-0 h-[30px] w-[45px] truncate"
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1 || pagination.total === 0}
              >
                이전
              </CommonButton>
              {startPage > 1 && (
                <>
                  <button onClick={() => setCurrentPage(1)} className="px-2">
                    1
                  </button>
                  {startPage > 2 && <span>...</span>}
                </>
              )}
              {pageNumbers.map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={clsx(
                    currentPage === page &&
                      "border rounded-[4px] text-brand-primary-magenta",
                    "px-2"
                  )}
                >
                  {page}
                </button>
              ))}
              {endPage < pagination.totalPages && (
                <>
                  {endPage < pagination.totalPages - 1 && <span>...</span>}
                  <button
                    onClick={() => setCurrentPage(pagination.totalPages)}
                    className="px-2"
                  >
                    {pagination.totalPages}
                  </button>
                </>
              )}
              <CommonButton
                className="text-sm flex-shrink-0 h-[30px] w-[45px] truncate"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={
                  currentPage === pagination.totalPages ||
                  pagination.total === 0
                }
              >
                다음
              </CommonButton>
            </div>
          </div>
        </div>
      </div>
      <CommonPopup
        isOpen={popup === "shelterChange"}
        title="대피소 변경"
        unUseHorizonRule
        padding={0}
      >
        <div className="flex flex-col">
          <div className="flex justify-center pb-5 px-5">
            <select
              onChange={(e) => {
                setTempSelectedShelterId(e.target.value);
              }}
              className="w-full p-2 border border-gray-300 rounded-md"
              value={tempSelectedShelterId}
            >
              {detail?.shelters.map((shelter) => (
                <option key={shelter.shelterId} value={shelter.shelterId}>
                  {shelter.shelterName}
                </option>
              ))}
            </select>
          </div>
          <div className="flex h-bottom-button">
            <CommonButton
              variant="secondary"
              className="w-[100px] rounded-none"
              onClick={() => setPopup("")}
            >
              취소
            </CommonButton>
            <CommonButton
              className="rounded-none flex-1"
              onClick={() => {
                const shelter = detail?.shelters.find(
                  (shelter) => shelter.shelterId === tempSelectedShelterId
                );
                setSelectedShelter(shelter);
                setPopup("");
              }}
            >
              확인
            </CommonButton>
          </div>
        </div>
      </CommonPopup>
      <EmergencyCheckPopup
        company={selectedEvacuee?.evacuationCompanyName ?? ""}
        name={selectedEvacuee?.name ?? "-"}
        evacuationEvacueeMapUuid={
          selectedEvacuee?.evacuationEvacueeMapUuid ?? ""
        }
        currentStatus={selectedEvacuee?.status as EvacuationStatusType}
        onClose={() => handleCloseStatusChangePopup()}
        onConfirm={() =>
          handleConfirm(selectedEvacuee?.evacuationEvacueeMapUuid ?? "")
        }
        isOpenProps={popup}
      />
      {simplePopupComponent}
    </>
  );
}
